<template>
  <div v-if="filteredPurchasedPackages.length" class="packages-section">
    <div class="section-heading">
      <h2>Packages</h2>
      <ion-note>{{ filteredPurchasedPackages.length }} active</ion-note>
    </div>
    <ion-list class="package-list" lines="none">
      <ion-item
        v-for="pckg in filteredPurchasedPackages" :key="pckg.id"
        class="package-card"
        :data-id="pckg.id"
        :detail="userStore.is.instructor"
        :button="userStore.is.instructor"
        @click="viewPackage(pckg)"
      >
        <ion-grid class="package-card__grid ion-no-padding">
          <ion-row class="package-card__header ion-align-items-center">
            <ion-col>
              <ion-note v-if="otherPartyName(pckg)" class="package-card__title">
                {{ otherPartyName(pckg) }}
              </ion-note>
              <br>
              <div class="package-card__note">{{ pckg.name }}</div>
              <!-- <div class="package-card__subtitle">{{ pckg.lessonsLeft }} of {{ pckg.lessons }} lessons remaining</div> -->
            </ion-col>
            <ion-col size="auto" class="package-card__status">
              <ion-badge :color="pckg.isPaid() ? 'success' : 'warning'">
                {{ pckg.isPaid() ? 'Paid' : 'Pending' }}
              </ion-badge>
            </ion-col>
          </ion-row>
          <ion-row class="package-card__body ion-align-items-center">
            <ion-col size="auto" class="package-card__count">
              <div class="package-card__number">{{ pckg.lessonsLeft }}</div>
              <small class="package-card__count-label">left</small>
            </ion-col>
            <ion-col>
              <ion-progress-bar class="package-card__progress" :value="packageProgress(pckg)" />
              <div class="package-card__meta">
                <ion-note color="medium">
                  {{ pckg.lessons }} total lessons
                </ion-note>
              </div>
            </ion-col>
            <ion-col v-if="displayBookButton && userStore.is.student" size="auto" class="package-card__action">
              <ion-button
                fill="outline" color="success"
                size="small"
                data-testid="package-book-button"
                :disabled="pckg.lessonsLeft == 0"
                @click.stop="bookLesson(pckg)"
              >
                Book
              </ion-button>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-item>
    </ion-list>
  </div>
</template>

<script setup>
import {
  IonBadge,
  IonButton,
  IonCol,
  IonGrid,
  IonItem,
  IonList,
  IonNote,
  IonProgressBar,
  IonRow,
} from '@ionic/vue'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { LessonsRepository } from '@/data/LessonsRepository'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()

const props = defineProps({
  displayBookButton: { required: false, type: Boolean, default: true },
  filterByUser: { required: false, type: Object, default: null },
})

const router = useRouter()

const LessonsRepo = new LessonsRepository()
const purchasedPackages = LessonsRepo.getLessonPackages()

const filteredPurchasedPackages = computed(() => {
  const result = purchasedPackages.value.filter(pckg => pckg.lessonsLeft > 0 || pckg.needsPayment()).sort((a, b) => b.paymentInfo?.status < a.paymentInfo?.status ? 1 : -1)
  if (!props.filterByUser) return result
  return result.filter(pckg => pckg.instructor.id === props.filterByUser.id || pckg.student.id === props.filterByUser.id)
})

function otherPartyName(pckg) {
  if (props.filterByUser) return ''
  if (userStore.is.student) return pckg.instructor.name
  if (userStore.is.instructor) return pckg.student.name
}

async function bookLesson(pckg) {
  router.push({ name: 'bookLesson', params: { instructorId: pckg.instructor.id }, query: { lessonType: pckg.name }})
}

function viewPackage(pckg) {
  if (!userStore.is.instructor) return
  router.push({ name: 'package', params: { packageId: pckg.id }})
}

function packageProgress(pckg) {
  if (!pckg.lessons || pckg.lessons === 0) return 0
  const completed = Math.max(pckg.lessons - pckg.lessonsLeft, 0)
  return Math.min(completed / pckg.lessons, 1)
}
</script>

<style scoped>
.packages-section {
  padding: 0 1rem 0.5rem;
}

.section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0 0.25rem;
}

.section-heading h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #13261e;
  letter-spacing: 0.01em;
}

.section-heading ion-note {
  font-size: 0.85rem;
  color: rgba(19, 38, 30, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.package-list {
  margin: 0;
  padding: 0;
  background: transparent;
}

.package-card {
  --padding-start: 1.25rem;
  --inner-padding-end: 1.25rem;
  --min-height: 118px;
  --background: linear-gradient(135deg, #ffffff, #f8faf8);
  --border-radius: 18px;
  --ripple-color: rgba(185, 152, 99, 0.18);
  --border-width: 0;
  --inner-border-width: 0;
  margin-bottom: 1rem;
  /* border: 1px solid rgba(19, 38, 30, 0.08);
  box-shadow: 0 18px 40px rgba(16, 38, 27, 0.08); */
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.package-card:focus,
.package-card:hover {
  transform: translateY(-2px);
  /* box-shadow: 0 20px 42px rgba(16, 38, 27, 0.12); */
}

.package-card__title {
  font-weight: 600;
  font-size: 1.05rem;
  color: #13261e;
}

.package-card__subtitle {
  margin-top: 0.3rem;
  font-size: 0.95rem;
  color: rgba(19, 38, 30, 0.75);
}

.package-card__note {
  margin-top: 0.45rem;
  display: inline-block;
  color: rgba(19, 38, 30, 0.55);
  font-size: 0.85rem;
  letter-spacing: 0.04em;
}

.package-card__status ion-badge {
  --color: #ffffff;
  --padding-start: 0.75rem;
  --padding-end: 0.75rem;
  --padding-top: 0.4rem;
  --padding-bottom: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.package-card__count {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-right: 0.75rem;
  /* border-right: 1px solid rgba(19, 38, 30, 0.08); */
}

.package-card__number {
  font-size: 2.4rem;
  font-weight: 600;
  color: #0f422a;
  line-height: 1;
}

.package-card__count-label {
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(19, 38, 30, 0.45);
}

.package-card__progress {
  margin: 0 0 0.65rem;
  --progress-background: #2dd55b;
  --progress-background-focused: rgba(19, 38, 30, 0.08);
  --progress-color: #0f422a;
  --progress-color-checked: #0f422a;
  border-radius: 999px;
  height: 6px;
}

.package-card__meta ion-note {
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  color: rgba(19, 38, 30, 0.55);
}

.package-card__action {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-left: 1rem;
}

.package-card__grid {
  width: 100%;
}
</style>
